import React, { useEffect, useState } from 'react';
import PageLayout from '../../layouts/PageLayout';
import HeroSection from 'components/HeroSection';
import Brochure from 'components/Brochure';
import TextPage from 'components/TextPage';
import ShowRooms from 'components/ShowRooms';
import { getB2BServicesData } from '../../api-sanity/api';
import { useRouteIsLoading } from 'context/RouteIsLoadingContext';
import { useTheme } from 'hooks/useTheme';
import Client from 'components/Client';
import usePageLoadProgress from 'hooks/usePageLoadProgress';
import { useSanityMetaTags } from 'hooks/useSanityMetaTags';

const pID = {
  P_1: '81ac97e5-cfe9-46eb-bd6d-d9f02f162a70',
};

const b2bServicesIds = {
  heroId: 'be267bef-75cb-42c1-a8b3-7ae4078f9c6d',
  brochureId: 'cd9307de-2cef-43b3-aa4a-84be7fa939d6',
  showRoomsId: '09bd3446-aee0-48b8-85f9-4674eae2c18b',
};

const B2BServices = () => {
  const [b2bServicesData, setB2bServicesData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setIsLoading: setRouteContextIsLoading } = useRouteIsLoading();
  const { isDarkMode } = useTheme();

  // Fetch and apply meta tags from Sanity
  useSanityMetaTags('b2b');

  const { loadingProgress, isDataReady, apiDataResults } = usePageLoadProgress(
    (onProgress) => getB2BServicesData(b2bServicesIds, onProgress)
  );

  useEffect(() => {
    if (isDataReady && apiDataResults) {
      const [data] = apiDataResults;
      setB2bServicesData(data);
      setIsLoading(false);
    }
  }, [isDataReady, apiDataResults]);

  useEffect(() => {
    setRouteContextIsLoading(isLoading);
  }, [isLoading, setRouteContextIsLoading]);

  const getParagraphById = (id) => {
    if (!b2bServicesData?.textPages) return null;
    return b2bServicesData.textPages.find((p) => p._id === id);
  };

  return (
    <PageLayout>
      <HeroSection
        imageUrl={
          isDarkMode
            ? b2bServicesData?.heroSection?.darkModeImageUrl ||
              b2bServicesData?.heroSection?.lightModeImageUrl
            : b2bServicesData?.heroSection?.lightModeImageUrl
        }
        title={b2bServicesData?.heroSection?.title}
        titleHighlight={b2bServicesData?.heroSection?.titleHighlight}
        subtitle={b2bServicesData?.heroSection?.subtitle}
        description={b2bServicesData?.heroSection?.description}
        buttonText={b2bServicesData?.heroSection?.buttonText}
        buttonLink={b2bServicesData?.heroSection?.buttonLink}
        showThemeToggle={b2bServicesData?.heroSection?.showThemeToggle}
        loaderProgress={loadingProgress}
        isLoading={isLoading}
      />
      {!isLoading && getParagraphById(pID.P_1) && (
        <TextPage
          key={getParagraphById(pID.P_1)?._id}
          type={getParagraphById(pID.P_1)?.type}
          titleActive={getParagraphById(pID.P_1)?.titleActive}
          titleInactive={getParagraphById(pID.P_1)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_1)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_1)?.paragraphText}
          buttonText={getParagraphById(pID.P_1)?.buttonText}
          buttonLink={getParagraphById(pID.P_1)?.buttonLink}
        />
      )}
      {!isLoading && b2bServicesData?.showRoomsData && (
        <ShowRooms
          showRoomsData={b2bServicesData.showRoomsData}
          scaleLastImage={true}
        />
      )}
      {!isLoading && <Client clientData={b2bServicesData?.clientData} />}
      {!isLoading && (
        <div
          className={`full-pager flex flex-column sizes-section ${
            isDarkMode ? '' : 'dark-menu-section'
          }`}
        >
          <div className='flex percentage-1'>
            <p className='font-size-60 img-page-font-grotesk'>
              {b2bServicesData?.presentationInNumbers?.section1?.number} <br />
              <span className='font-secondary font-size-30'>
                {b2bServicesData?.presentationInNumbers?.section1?.title}
              </span>
            </p>
            <p className='font-size-18'>
              {b2bServicesData?.presentationInNumbers?.section1?.description}
            </p>
          </div>
          <div className='flex percentage-2'>
            <p className='font-size-60 img-page-font-grotesk'>
              {b2bServicesData?.presentationInNumbers?.section2?.number}
              <br />
              <span className='font-secondary font-size-30'>
                {b2bServicesData?.presentationInNumbers?.section2?.title}
              </span>
            </p>
            <p className='font-size-18'>
              {b2bServicesData?.presentationInNumbers?.section2?.description}
            </p>
          </div>
          <div className='flex percentage-3'>
            <p className='font-size-60 img-page-font-grotesk'>
              {b2bServicesData?.presentationInNumbers?.section3?.number}
              <br />
              <span className='font-secondary font-size-30'>
                {b2bServicesData?.presentationInNumbers?.section3?.title}
              </span>
            </p>
            <p className='font-size-18'>
              {b2bServicesData?.presentationInNumbers?.section3?.description}
            </p>
          </div>
          <div className='flex percentage-4'>
            <p className='font-size-60 img-page-font-grotesk'>
              {b2bServicesData?.presentationInNumbers?.section4?.number}
              <br />
              <span className='font-secondary font-size-30'>
                {b2bServicesData?.presentationInNumbers?.section4?.title}
              </span>
            </p>
            <p className='font-size-18'>
              {b2bServicesData?.presentationInNumbers?.section4?.description}
            </p>
          </div>
        </div>
      )}
      {!isLoading && <Brochure data={b2bServicesData?.brochureData} />}
    </PageLayout>
  );
};

export default B2BServices;
